import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import TableExportButton from "@/components/TableExportButton";
import {
  Upload, Download, Truck, Printer, Trash2, Pencil,
  KeyRound, Hash, Search, X, Check, FileText, FileSpreadsheet, ChevronDown,
} from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Forgiving customer-name normaliser: lowercase, then collapse the literal
// word "and" and the ampersand "&" to nothing so "CG Power & Industrial
// Solutions Ltd" matches "CG Power and Industrial Solutions Ltd", then strip
// every remaining non-alphanumeric. "Acme Industries Pvt. Ltd." matches
// "ACME INDUSTRIES PVT LTD" and "acme-industries  pvt ltd" too.
const normName = (s) => String(s || "")
  .toLowerCase()
  .replace(/&/g, " ")
  .replace(/\band\b/g, " ")
  .replace(/[^a-z0-9]/g, "");
// Strip common Indian business suffixes so "Hindalco Industries Ltd" matches "Hindalco Industries"
const stripSuffix = (k) => k.replace(/(pvtltd|privatelimited|limited|ltd|llp|inc|company|co|corp|corporation|enterprises|industries|industry)$/g, "");
const findCustomer = (list, name) => {
  const k = normName(name);
  if (!k) return null;
  // 1) exact match
  let hit = list.find((x) => normName(x.name) === k);
  if (hit) return hit;
  // 2) suffix-stripped exact match (covers Ltd / Pvt Ltd / Industries / Co. drift)
  const ks = stripSuffix(k);
  if (ks && ks !== k) {
    hit = list.find((x) => stripSuffix(normName(x.name)) === ks);
    if (hit) return hit;
  }
  // 3) one-way-prefix fallback (longer name starts with shorter) — only when
  //    the shorter is ≥ 5 chars to avoid bogus matches like "AB" → "ABC Corp".
  hit = list.find((x) => {
    const nx = normName(x.name);
    if (!nx) return false;
    if (k.length >= 5 && nx.startsWith(k)) return true;
    if (nx.length >= 5 && k.startsWith(nx)) return true;
    return false;
  });
  return hit || null;
};

export default function PORegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";

  const [pos, setPos] = useState([]); // Sales POs (Excel-imported reference data)
  const [invoices, setInvoices] = useState([]);
  const [counter, setCounter] = useState({ value: 0, seeded: false });
  const [customers, setCustomers] = useState([]); // Customer Master rows w/ addresses

  // ---- Mode toggle
  const [mode, setMode] = useState("auto"); // 'manual' | 'auto'

  // ---- Common form state used by BOTH modes; auto mode fills the read-only fields from a chosen PO
  const blankForm = {
    date: todayISO(),
    customer: "",
    customer_id: "",
    part_no: "",
    part_name: "",
    po_no: "",
    delivery_date: "",
    order_qty: "",
    balance_qty: "",
    part_rate: "",
    disp_qty: "",
    gst_pct: "",         // legacy combined — kept so PO auto-fetch still pre-fills
    cgst_pct: "",        // NEW — optional per-head GST inputs
    sgst_pct: "",
    igst_pct: "",
    vehicle_no: "",
    dn: "",
    hsn_code: "",
    transport_name: "",
    bill_to_address: "",
    bill_to_place_of_supply: "",
    ship_to_address: "",
    ship_to_place_of_supply: "",
    remark: "",
    intra_state: true,
    company_name: "",   // sub-company tag (Tarana Polymers / Tarana Engineering)
  };
  const [form, setForm] = useState(blankForm);

  // ---- Additional Items (multi-item invoicing) — same Customer + same PO No, different Parts
  // Each row: { sales_po_id, part_no, part_name, balance_qty, dispatch_qty, part_rate, gst_pct, hsn_code }
  const [extraItems, setExtraItems] = useState([]);

  // For Auto mode — which PO is currently picked (so we know its id for the dispatch API)
  const [autoPoId, setAutoPoId] = useState("");

  // Dropdown open states + refs for outside-click dismissal
  const [openDD, setOpenDD] = useState(null); // 'customer' | 'partno' | 'pono' | null
  const ddRefs = { customer: useRef(null), partno: useRef(null), pono: useRef(null) };

  // Search strings per dropdown
  const [dq, setDq] = useState({ customer: "", partno: "", pono: "" });

  const [saving, setSaving] = useState(false);
  const [printPrompt, setPrintPrompt] = useState(null); // { id, invoice_no }

  // ---- Excel template / import / seed counter
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seedVal, setSeedVal] = useState("");

  // ---- Log search
  const [logSearch, setLogSearch] = useState("");

  // ---- Sales-Log multi-select + bulk "Set Company" ----
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [companyOptions, setCompanyOptions] = useState([]);
  const [bulkCompanyOpen, setBulkCompanyOpen] = useState(false);
  const [bulkCompanyValue, setBulkCompanyValue] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const reload = async () => {
    const [poR, invR, ctR, cuR, coR] = await Promise.all([
      api.get("/sales/po-register"),
      api.get("/sales/invoices"),
      api.get("/sales/invoice-counter").catch(() => ({ data: { value: 0, seeded: false } })),
      api.get("/customers").catch(() => ({ data: [] })),
      api.get("/companies-book").catch(() => ({ data: [] })),
    ]);
    setPos(poR.data || []);
    setInvoices(invR.data || []);
    setCounter(ctR.data || { value: 0, seeded: false });
    setCustomers(cuR.data || []);
    setCompanyOptions((coR.data || []).map((c) => c.name).filter(Boolean));
    setSelectedIds(new Set());
  };
  useEffect(() => { reload(); }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const onDoc = (e) => {
      const cur = openDD && ddRefs[openDD]?.current;
      if (cur && !cur.contains(e.target)) setOpenDD(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openDD]);

  // ---- Auto-mode cascade
  const customerOpts = useMemo(() => {
    // Dedupe by normalized name so "CG POWER & ..." and "CG POWER AND ..."
    // collapse into a single dropdown option. Prefer the longest spelling.
    const m = new Map();
    for (const p of pos) {
      if (!p.customer) continue;
      const k = normName(p.customer);
      if (!k) continue;
      const cur = m.get(k);
      if (!cur || p.customer.length > cur.length) m.set(k, p.customer);
    }
    return Array.from(m.values()).sort()
      .filter((c) => c.toLowerCase().includes(dq.customer.toLowerCase()));
  }, [pos, dq.customer]);

  // Quick same-customer test that survives name drift (`&` vs `AND`,
  // `Ltd` vs `Limited`, trailing dots, etc.) — built on top of normName.
  const sameCust = (a, b) => {
    const na = normName(a), nb = normName(b);
    return !!na && na === nb;
  };

  const partOpts = useMemo(() => {
    if (!form.customer) return [];
    const seen = new Map();
    for (const p of pos) {
      if (!sameCust(p.customer, form.customer)) continue;
      if (Number(p.balance_qty || 0) <= 0) continue;
      if (!seen.has(p.part_no)) seen.set(p.part_no, p.part_name || "");
    }
    return Array.from(seen.entries())
      .filter(([no, nm]) => `${no} ${nm}`.toLowerCase().includes(dq.partno.toLowerCase()))
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [pos, form.customer, dq.partno]);

  const poOpts = useMemo(() => {
    if (!form.customer || !form.part_no) return [];
    return pos
      .filter((p) => sameCust(p.customer, form.customer) && p.part_no === form.part_no && Number(p.balance_qty || 0) > 0)
      .filter((p) => `${p.po_no}`.toLowerCase().includes(dq.pono.toLowerCase()))
      .sort((a, b) => a.po_no.localeCompare(b.po_no));
  }, [pos, form.customer, form.part_no, dq.pono]);

  // ---- Additional-item dropdown options. PO list = every PO of the picked
  // customer (no balance filter — partially / fully dispatched POs still show
  // so the user can re-bill them if they have updated qty). Independent
  // filtering still happens on each row's Part dropdown.
  const customerPoOpts = useMemo(() => {
    if (!form.customer) return [];
    const s = new Set();
    for (const p of pos) {
      if (sameCust(p.customer, form.customer) && p.po_no) s.add(p.po_no);
    }
    return Array.from(s).sort();
  }, [pos, form.customer]);

  const addExtraItem = () => {
    // Add a blank row regardless — user explicitly asked for the Add Item
    // button to never be disabled. Filters in the dropdowns still keep the
    // selectable parts sensible.
    setExtraItems((prev) => [...prev, {
      sales_po_id: "",
      po_no: form.po_no || "",
      part_no: "",
      part_name: "",
      balance_qty: 0,
      dispatch_qty: "",
      part_rate: "",
      gst_pct: form.gst_pct,        // legacy combined — kept for back-compat
      cgst_pct: form.cgst_pct,      // NEW — per-head defaults inherit from primary
      sgst_pct: form.sgst_pct,
      igst_pct: form.igst_pct,
      hsn_code: form.hsn_code || "",
    }]);
  };

  const updateExtra = (idx, patch) => {
    setExtraItems((prev) => prev.map((row, i) => {
      if (i !== idx) return row;
      const next = { ...row, ...patch };
      // If PO changed → clear the part selection (and dependent fields) so the
      // user picks a fresh part for that PO.
      if (patch.po_no !== undefined && patch.po_no !== row.po_no) {
        next.sales_po_id = "";
        next.part_no = "";
        next.part_name = "";
        next.balance_qty = 0;
        next.part_rate = "";
      }
      // If part changed → re-sync rate/hsn/balance from the picked PO row
      if (patch.sales_po_id && patch.sales_po_id !== row.sales_po_id) {
        const p = pos.find((x) => x.id === patch.sales_po_id);
        if (p) {
          next.po_no = p.po_no;
          next.part_no = p.part_no;
          next.part_name = p.part_name || "";
          next.balance_qty = Number(p.balance_qty || 0);
          next.part_rate = String(p.part_rate || "");
          next.hsn_code = p.hsn_code || row.hsn_code || "";
        }
      }
      return next;
    }));
  };

  const removeExtra = (idx) => {
    setExtraItems((prev) => prev.filter((_, i) => i !== idx));
  };

  // ---- Derived auto fields
  const dispValue = Number(form.disp_qty || 0) * Number(form.part_rate || 0);
  // Effective combined GST = sum of CGST + SGST + IGST if any of the three is
  // set, otherwise fall back to the legacy combined `gst_pct` field.
  const _hasPerHead = (form.cgst_pct !== "" || form.sgst_pct !== "" || form.igst_pct !== "");
  const effectiveGstPct = _hasPerHead
    ? (Number(form.cgst_pct || 0) + Number(form.sgst_pct || 0) + Number(form.igst_pct || 0))
    : Number(form.gst_pct || 0);
  const gstAmount = (dispValue * effectiveGstPct) / 100;
  const invoiceValue = dispValue + gstAmount;

  // Resolve customer → addresses for the picked customer (used by Bill-To /
  // Ship-To dropdowns AND to auto-fill place_of_supply for the print).
  // Re-runs whenever customer or customer list changes so manual typing also
  // pulls the right defaults.
  useEffect(() => {
    if (!form.customer) return;
    const cm = findCustomer(customers, form.customer);
    if (!cm) return;
    const addrs = Array.isArray(cm.addresses) && cm.addresses.length
      ? cm.addresses
      : (cm.address ? [{ label: "Primary", address: cm.address, place_of_supply: cm.place_of_supply || "" }] : []);
    const primary = addrs[0] || { address: "", place_of_supply: "" };
    setForm((prev) => ({
      ...prev,
      customer_id: prev.customer_id || cm.id || "",
      // Only fill if user hasn't already picked something
      bill_to_address: prev.bill_to_address || primary.address || "",
      bill_to_place_of_supply: prev.bill_to_place_of_supply || primary.place_of_supply || cm.place_of_supply || "",
      ship_to_address: prev.ship_to_address || primary.address || "",
      ship_to_place_of_supply: prev.ship_to_place_of_supply || primary.place_of_supply || cm.place_of_supply || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer, customers]);

  // ---- Reset helpers
  const pickCustomer = (c) => {
    // Resolve the matching Customer Master entry (forgiving normalised name match)
    const cm = findCustomer(customers, c);
    const addrs = Array.isArray(cm?.addresses) && cm.addresses.length
      ? cm.addresses
      : (cm?.address ? [{ label: "Primary", address: cm.address, place_of_supply: cm.place_of_supply || "" }] : []);
    const primary = addrs[0] || { address: "", place_of_supply: "" };
    setForm({
      ...form,
      customer: c,
      customer_id: cm?.id || "",
      part_no: "", part_name: "", po_no: "",
      delivery_date: "", order_qty: "", balance_qty: "", part_rate: "",
      // Pre-fill Bill To + Ship To with the customer's primary address
      bill_to_address: primary.address || "",
      bill_to_place_of_supply: primary.place_of_supply || cm?.place_of_supply || "",
      ship_to_address: primary.address || "",
      ship_to_place_of_supply: primary.place_of_supply || cm?.place_of_supply || "",
    });
    setAutoPoId("");
    setExtraItems([]);
    setDq({ customer: "", partno: "", pono: "" });
    setOpenDD(null);
  };
  const pickPartNo = (no, nm) => {
    setForm({ ...form, part_no: no, part_name: nm || "", po_no: "", delivery_date: "", order_qty: "", balance_qty: "", part_rate: "" });
    setAutoPoId("");
    setExtraItems([]);
    setDq({ ...dq, partno: "", pono: "" });
    setOpenDD(null);
  };
  const pickPo = (p) => {
    setForm({
      ...form,
      po_no: p.po_no,
      delivery_date: p.delivery_date || "",
      order_qty: String(p.order_qty || ""),
      balance_qty: String(p.balance_qty ?? p.order_qty ?? ""),
      part_rate: String(p.part_rate || ""),
      hsn_code: p.hsn_code || form.hsn_code,
      // Auto-fetch GST % from the Customer PO Register row → user can still override
      gst_pct: p.gst_pct != null && p.gst_pct !== "" ? String(p.gst_pct) : form.gst_pct,
    });
    setAutoPoId(p.id);
    setExtraItems([]); // changing the primary PO invalidates additional items
    setDq({ ...dq, pono: "" });
    setOpenDD(null);
  };

  // ---- Submit
  const onSave = async () => {
    if (!form.customer.trim()) return toast.error("Customer is required");
    if (!form.part_no.trim()) return toast.error("Part No is required");
    if (!form.po_no.trim()) return toast.error("PO No is required");
    const ordQty = Number(form.order_qty);
    const balQty = Number(form.balance_qty !== "" ? form.balance_qty : form.order_qty);
    const dispQ = Number(form.disp_qty);
    if (!ordQty || !Number(form.part_rate)) return toast.error("Order Qty and Part Rate must be positive");
    if (!dispQ || dispQ <= 0) return toast.error("Dispatch Qty must be greater than zero");
    if (dispQ - balQty > 1e-9) return toast.error(`Dispatch Qty (${dispQ}) cannot exceed Balance Qty (${balQty})`);

    // Validate additional items
    for (let i = 0; i < extraItems.length; i++) {
      const row = extraItems[i];
      const q = Number(row.dispatch_qty);
      if (!row.sales_po_id) return toast.error(`Additional item #${i + 1}: pick a Part`);
      if (!q || q <= 0) return toast.error(`Additional item #${i + 1}: Dispatch Qty must be > 0`);
      if (q - Number(row.balance_qty || 0) > 1e-9)
        return toast.error(`Additional item #${i + 1} (${row.part_no}): Qty ${q} exceeds Balance ${row.balance_qty}`);
    }

    setSaving(true);
    try {
      // 1) Resolve the underlying Sales PO id
      let poId = autoPoId;
      if (!poId) {
        // Manual mode (or auto with a brand-new PO) → create a Sales PO first
        const { data: newPo } = await api.post("/sales/po-register", {
          date: form.date || todayISO(),
          customer: form.customer.trim(),
          part_no: form.part_no.trim(),
          part_name: form.part_name.trim(),
          po_no: form.po_no.trim(),
          delivery_date: form.delivery_date || "",
          order_qty: ordQty,
          part_rate: Number(form.part_rate),
          gst_pct: 0,
          hsn_code: form.hsn_code || "",
        });
        poId = newPo.id;
      }
      // 2) Dispatch + auto-create invoice (with optional multi-item array)
      const { data: inv } = await api.post("/sales/dispatch", {
        sales_po_id: poId,
        dispatch_qty: dispQ,
        // User-picked date on the form drives the invoice_date — without this
        // the backend stamps today's date and the user's selection is lost.
        invoice_date: form.date || undefined,
        vehicle_no: form.vehicle_no || "",
        dn: form.dn || "",
        hsn_code: form.hsn_code || "",
        gst_pct: form.gst_pct === "" ? null : Number(form.gst_pct),
        // NEW — per-head rates (any/all may be blank → null)
        cgst_pct: form.cgst_pct === "" ? null : Number(form.cgst_pct),
        sgst_pct: form.sgst_pct === "" ? null : Number(form.sgst_pct),
        igst_pct: form.igst_pct === "" ? null : Number(form.igst_pct),
        intra_state: !!form.intra_state,
        transport_name: form.transport_name || "",
        remark: form.remark || "",
        bill_to_address: form.bill_to_address || "",
        bill_to_place_of_supply: form.bill_to_place_of_supply || "",
        ship_to_address: form.ship_to_address || "",
        ship_to_place_of_supply: form.ship_to_place_of_supply || "",
        company_name: (form.company_name || "").trim(),
        items: extraItems.map((row) => ({
          sales_po_id: row.sales_po_id,
          dispatch_qty: Number(row.dispatch_qty),
          part_rate: row.part_rate === "" ? null : Number(row.part_rate),
          gst_pct: row.gst_pct === "" ? null : Number(row.gst_pct),
          cgst_pct: row.cgst_pct === "" || row.cgst_pct == null ? null : Number(row.cgst_pct),
          sgst_pct: row.sgst_pct === "" || row.sgst_pct == null ? null : Number(row.sgst_pct),
          igst_pct: row.igst_pct === "" || row.igst_pct == null ? null : Number(row.igst_pct),
          hsn_code: row.hsn_code || "",
          po_no: row.po_no || form.po_no, // per-row PO No (defaults to primary)
        })),
      });
      toast.success(`Invoice ${inv.invoice_no} generated`);
      setPrintPrompt({ id: inv.id, invoice_no: inv.invoice_no });
      // Reset form (keep mode + date)
      setForm({ ...blankForm, date: todayISO() });
      setExtraItems([]);
      setAutoPoId("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ---- Excel template / import
  const authedDownload = async (path, filename) => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Download failed"); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadExcel = async (file) => {
    setUploading(true);
    const t = toast.loading("Importing Sales POs…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/sales/po-register/upload-excel", fd);
      toast.success(`Imported ${data.inserted} · skipped ${data.skipped.length}`, { id: t });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed", { id: t });
    } finally {
      setUploading(false);
    }
  };

  const onSeedCounter = async () => {
    const n = Number(seedVal);
    if (!Number.isFinite(n) || n < 0) return toast.error("Enter a valid number");
    try {
      await api.post("/sales/invoice-counter/seed", { last_invoice_number: n });
      toast.success(`Counter set — next invoice will be INV-${n + 1}/YYYY`);
      setSeedOpen(false);
      setSeedVal("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed");
    }
  };

  const onDeleteInvoice = async (inv) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?\nThe parent PO's balance will be restored.`)) return;
    try {
      await api.delete(`/sales/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- Edit invoice modal (password-gated) ----
  const [editInv, setEditInv] = useState(null);     // currently-edited invoice
  const [editPwd, setEditPwd] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (inv) => {
    const items = Array.isArray(inv.items) && inv.items.length > 0
      ? inv.items.map((li) => ({
          sales_po_id: li.sales_po_id || inv.sales_po_id || "",
          po_no: li.po_no || inv.po_no || "",
          part_no: li.part_no || "",
          part_name: li.part_name || "",
          hsn_code: li.hsn_code || "",
          dispatch_qty: String(li.dispatch_qty ?? ""),
          part_rate: String(li.part_rate ?? ""),
          gst_pct: String(li.gst_pct ?? ""),
        }))
      : [{
          sales_po_id: inv.sales_po_id || "",
          po_no: inv.po_no || "",
          part_no: inv.part_no || "",
          part_name: inv.part_name || "",
          hsn_code: inv.hsn_code || "",
          dispatch_qty: String(inv.dispatch_qty ?? ""),
          part_rate: String(inv.part_rate ?? ""),
          gst_pct: String(inv.gst_pct ?? ""),
        }];
    setEditInv({
      id: inv.id,
      invoice_no: inv.invoice_no,
      invoice_date: inv.invoice_date || todayISO(),
      customer: inv.customer || "",
      vehicle_no: inv.vehicle_no || "",
      dn: inv.dn || "",
      transport_name: inv.transport_name || "",
      bill_to_address: inv.bill_to_address || "",
      bill_to_place_of_supply: inv.bill_to_place_of_supply || "",
      ship_to_address: inv.ship_to_address || "",
      ship_to_place_of_supply: inv.ship_to_place_of_supply || "",
      remark: inv.remark || "",
      cgst_pct: inv.cgst_pct != null ? String(inv.cgst_pct) : "",
      sgst_pct: inv.sgst_pct != null ? String(inv.sgst_pct) : "",
      igst_pct: inv.igst_pct != null ? String(inv.igst_pct) : "",
      // Multi-line items — each row is independently editable
      items: items,
      // Convenience: keep the primary item's snapshot at the top level for
      // any code path that still reads single-line fields off editInv.
      part_no: items[0].part_no,
      part_name: items[0].part_name,
      po_no: items[0].po_no,
      part_rate: items[0].part_rate,
      dispatch_qty: items[0].dispatch_qty,
      hsn_code: items[0].hsn_code,
      gst_pct: items[0].gst_pct,
    });
    setEditPwd("");
  };

  const saveEdit = async () => {
    if (!editInv) return;
    if (!editPwd.trim()) {
      toast.error("Password required");
      return;
    }
    const lines = editInv.items || [];
    if (lines.length === 0) {
      toast.error("At least one line item is required");
      return;
    }
    for (const [idx, li] of lines.entries()) {
      const q = Number(li.dispatch_qty);
      if (!q || q <= 0) {
        toast.error(`Line ${idx + 1}: Dispatch Qty must be > 0`);
        return;
      }
    }
    const primary = lines[0];
    setEditSaving(true);
    try {
      await api.put(`/sales/invoices/${editInv.id}`, {
        // Single-line fields kept for backward compat — backend will overwrite
        // these from items[0] anyway.
        sales_po_id: primary.sales_po_id || "",
        dispatch_qty: Number(primary.dispatch_qty),
        part_rate: primary.part_rate === "" ? null : Number(primary.part_rate),
        vehicle_no: editInv.vehicle_no || "",
        hsn_code: primary.hsn_code || "",
        gst_pct: primary.gst_pct === "" ? null : Number(primary.gst_pct),
        // Per-head GST (invoice-level — applies to intra/inter split)
        cgst_pct: editInv.cgst_pct === "" ? null : Number(editInv.cgst_pct),
        sgst_pct: editInv.sgst_pct === "" ? null : Number(editInv.sgst_pct),
        igst_pct: editInv.igst_pct === "" ? null : Number(editInv.igst_pct),
        invoice_date: editInv.invoice_date || null,
        invoice_no: editInv.invoice_no || null,
        customer: editInv.customer,
        part_no: primary.part_no,
        part_name: primary.part_name,
        po_no: primary.po_no,
        dn: editInv.dn,
        transport_name: editInv.transport_name,
        bill_to_address: editInv.bill_to_address,
        bill_to_place_of_supply: editInv.bill_to_place_of_supply,
        ship_to_address: editInv.ship_to_address,
        ship_to_place_of_supply: editInv.ship_to_place_of_supply,
        remark: editInv.remark,
        // Full multi-line replacement — backend rebuilds items[] from this.
        items_edit: lines.map((li) => ({
          sales_po_id: li.sales_po_id || "",
          po_no: li.po_no || "",
          part_no: li.part_no || "",
          part_name: li.part_name || "",
          hsn_code: li.hsn_code || "",
          dispatch_qty: Number(li.dispatch_qty || 0),
          part_rate: li.part_rate === "" ? null : Number(li.part_rate),
          gst_pct: li.gst_pct === "" ? 0 : Number(li.gst_pct),
        })),
      }, { headers: { "X-Approval-Password": editPwd } });
      toast.success(`Invoice ${editInv.invoice_no} updated`);
      setEditInv(null);
      setEditPwd("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const filteredInvoices = useMemo(() => {
    if (!logSearch.trim()) return invoices;
    const q = logSearch.toLowerCase();
    return invoices.filter((i) =>
      [i.invoice_no, i.customer, i.part_no, i.part_name, i.po_no, i.vehicle_no, i.hsn_code]
        .some((v) => String(v || "").toLowerCase().includes(q))
    );
  }, [invoices, logSearch]);

  // ---- Multi-select handlers for Sales Log ----
  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = filteredInvoices.length > 0 &&
    filteredInvoices.every((i) => selectedIds.has(i.id));
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        filteredInvoices.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      filteredInvoices.forEach((i) => next.add(i.id));
      return next;
    });
  };
  const applyBulkCompany = async () => {
    if (selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const { data } = await api.post("/sales/invoices/bulk-set-company", {
        invoice_ids: Array.from(selectedIds),
        company_name: bulkCompanyValue,   // "" clears the tag
      });
      toast.success(
        bulkCompanyValue
          ? `Tagged ${data.updated} invoice${data.updated === 1 ? "" : "s"} → ${bulkCompanyValue}`
          : `Cleared company on ${data.updated} invoice${data.updated === 1 ? "" : "s"}`
      );
      setBulkCompanyOpen(false);
      setBulkCompanyValue("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
  const readonlyCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-slate-50 text-slate-700";

  const autoLocked = mode === "auto";

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Sales</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Sales Management</h1>
          <p className="text-sm text-slate-500 mt-1">Dispatch a customer PO and auto-generate the tax invoice. PO data is sourced from <span className="font-semibold text-slate-700">Customer PO Register</span>.</p>
        </div>
      </div>

      {/* Entry card */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-semibold text-slate-900">Dispatch &amp; Invoice Entry</div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex border border-slate-200 rounded-md overflow-hidden text-xs">
              <button onClick={() => setMode("manual")} data-testid="tab-manual"
                className={`px-4 py-1.5 font-semibold uppercase tracking-wider ${mode === "manual" ? "bg-[#1D2951] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Manual</button>
              <button onClick={() => setMode("auto")} data-testid="tab-auto"
                className={`px-4 py-1.5 font-semibold uppercase tracking-wider border-l border-slate-200 ${mode === "auto" ? "bg-[#1D2951] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>Auto</button>
            </div>
            <div className="text-xs px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 inline-flex items-center gap-2" data-testid="invoice-counter-chip">
              <Hash size={12} className="text-[#1D2951]" />
              <span className="text-slate-500">Last invoice #:</span>
              <span className="font-mono font-semibold" data-testid="invoice-counter-value">
                {/* Most recently generated invoice number. Invoices are loaded
                    newest-first (sort by created_at desc on the API) so
                    `invoices[0]` is the latest. Falls back to "—" when no
                    invoice has been created yet. */}
                {invoices.length > 0 ? invoices[0].invoice_no : (counter.value ? `#${counter.value}` : "—")}
              </span>
              {!counter.seeded && invoices.length === 0 && <span className="text-[10px] uppercase tracking-wider text-amber-700 ml-1">unseeded</span>}
            </div>
            <button onClick={() => { setSeedVal(String(counter.value || 0)); setSeedOpen(true); }}
              disabled={invoices.length > 0}
              title={invoices.length > 0 ? "Cannot reseed once invoices exist" : "Seed the starting invoice number"}
              data-testid="seed-invoice-counter-btn"
              className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-md border border-[#1D2951] text-[#1D2951] hover:bg-[#1D2951] hover:text-white inline-flex items-center gap-2 disabled:opacity-50">
              <KeyRound size={12} /> Init Counter
            </button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid={`mode-${mode}`}>
          {/* Company (sub-entity tag) — drives which sub-company this invoice
              counts under on MR & Accounts Dashboard */}
          <Field label="Company">
            <select
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              data-testid="form-company"
              className={inputCls}
            >
              <option value="">— Unassigned (visible only under &quot;All Companies&quot;) —</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </Field>

          {/* Date */}
          <Field label="Date">
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="form-date" />
          </Field>

          {/* Customer */}
          <div ref={ddRefs.customer}>
            <Lbl>Customer *</Lbl>
            {autoLocked ? (
              <DropdownInput
                value={form.customer}
                onChange={(v) => { setForm({ ...form, customer: v }); setDq({ ...dq, customer: v }); setOpenDD("customer"); }}
                onFocus={() => { setDq({ ...dq, customer: "" }); setOpenDD("customer"); }}
                onClear={() => pickCustomer("")}
                placeholder="Search customer from imported data…"
                testid="auto-customer"
                open={openDD === "customer"}
                options={customerOpts.map((c) => ({ key: c, label: c, onClick: () => pickCustomer(c) }))}
              />
            ) : (
              <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })}
                className={inputCls} placeholder="ACME Industries" data-testid="manual-customer" />
            )}
          </div>

          {/* Part No */}
          <div ref={ddRefs.partno}>
            <Lbl>Part No *</Lbl>
            {autoLocked ? (
              <DropdownInput
                value={form.part_no}
                onChange={(v) => { setForm({ ...form, part_no: v }); setDq({ ...dq, partno: v }); setOpenDD("partno"); }}
                onFocus={() => { setDq({ ...dq, partno: "" }); setOpenDD("partno"); }}
                onClear={() => pickPartNo("", "")}
                placeholder={form.customer ? "Search part no…" : "Pick customer first"}
                disabled={!form.customer}
                mono
                testid="auto-partno"
                open={openDD === "partno"}
                options={partOpts.map(([no, nm]) => ({ key: no, label: <span><span className="font-mono">{no}</span>{nm ? <span className="text-xs text-slate-500 ml-2">{nm}</span> : null}</span>, onClick: () => pickPartNo(no, nm) }))}
              />
            ) : (
              <input value={form.part_no} onChange={(e) => setForm({ ...form, part_no: e.target.value })}
                className={`${inputCls} font-mono`} placeholder="P-1001" data-testid="manual-partno" />
            )}
          </div>

          {/* Part Name */}
          <Field label="Part Name">
            <input value={form.part_name}
              onChange={(e) => setForm({ ...form, part_name: e.target.value })}
              className={autoLocked && form.part_no ? readonlyCls : inputCls}
              placeholder="Hex Bolt M10"
              readOnly={autoLocked && !!form.part_no}
              data-testid="form-part-name"
            />
          </Field>

          {/* PO No */}
          <div ref={ddRefs.pono}>
            <Lbl>PO No *</Lbl>
            {autoLocked ? (
              <DropdownInput
                value={form.po_no}
                onChange={(v) => { setForm({ ...form, po_no: v }); setDq({ ...dq, pono: v }); setOpenDD("pono"); }}
                onFocus={() => { setDq({ ...dq, pono: "" }); setOpenDD("pono"); }}
                onClear={() => { setForm({ ...form, po_no: "", delivery_date: "", order_qty: "", part_rate: "" }); setAutoPoId(""); }}
                placeholder={form.part_no ? "Search PO no…" : "Pick part first"}
                disabled={!form.part_no}
                mono
                testid="auto-pono"
                open={openDD === "pono"}
                options={poOpts.map((p) => ({
                  key: p.id,
                  label: (
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono">{p.po_no}</span>
                      <span className="text-[10px] text-slate-500">
                        Delivery: {p.delivery_date || "—"} · Qty {p.order_qty} · ₹{fmt(p.part_rate)} · Bal {p.balance_qty}
                      </span>
                    </div>
                  ),
                  onClick: () => pickPo(p),
                }))}
              />
            ) : (
              <input value={form.po_no} onChange={(e) => setForm({ ...form, po_no: e.target.value })}
                className={`${inputCls} font-mono`} placeholder="ACME-PO-01" data-testid="manual-pono" />
            )}
          </div>

          {/* Delivery Date */}
          <Field label="Delivery Date">
            <input type="date" value={form.delivery_date}
              onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
              className={autoLocked && autoPoId ? readonlyCls : inputCls}
              readOnly={autoLocked && !!autoPoId}
              data-testid="form-delivery-date"
            />
          </Field>

          {/* Order Qty */}
          <Field label="Order Qty *">
            <input type="number" min="0" step="any" value={form.order_qty}
              onChange={(e) => setForm({ ...form, order_qty: e.target.value })}
              className={autoLocked && autoPoId ? readonlyCls : inputCls}
              readOnly={autoLocked && !!autoPoId}
              data-testid="form-order-qty"
            />
          </Field>

          {/* Balance Qty (read-only — sourced from Customer PO Register) */}
          {autoPoId && (
            <Field label="Balance Qty">
              <div className={`${readonlyCls} font-semibold text-rose-700`} data-testid="form-balance-qty">
                {form.balance_qty !== "" ? fmt(form.balance_qty) : "—"}
              </div>
            </Field>
          )}

          {/* Part Rate */}
          <Field label="Part Rate (₹) *">
            <input type="number" min="0" step="any" value={form.part_rate}
              onChange={(e) => setForm({ ...form, part_rate: e.target.value })}
              className={autoLocked && autoPoId ? readonlyCls : inputCls}
              readOnly={autoLocked && !!autoPoId}
              data-testid="form-part-rate"
            />
          </Field>

          {/* Disp Qty — capped at Balance Qty (or Order Qty in manual mode) */}
          {(() => {
            const cap = Number(form.balance_qty !== "" ? form.balance_qty : form.order_qty || 0);
            const dispOver = form.disp_qty && cap && Number(form.disp_qty) - cap > 1e-9;
            return (
              <Field label="Disp Qty *">
                <input type="number" min="0" step="any" max={cap || undefined} value={form.disp_qty}
                  onChange={(e) => setForm({ ...form, disp_qty: e.target.value })}
                  className={`${inputCls} ${dispOver ? "border-rose-500 focus:ring-rose-200 focus:border-rose-500" : ""}`}
                  data-testid="form-disp-qty"
                />
                {dispOver && (
                  <div className="text-[10px] text-rose-600 mt-1">
                    Dispatch Qty must be ≤ {autoPoId ? "Balance Qty" : "Order Qty"} ({fmt(cap)})
                  </div>
                )}
              </Field>
            );
          })()}

          {/* Disp Value (auto) */}
          <Field label="Disp Value (auto)">
            <div className={readonlyCls} data-testid="form-disp-value">₹{fmt(dispValue)}</div>
          </Field>

          {/* GST split — three optional inputs. Enter any combination. */}
          <Field label={<>GST % <span className="text-slate-400 font-normal">(CGST · SGST · IGST — all optional)</span></>} className="md:col-span-2">
            <div className="grid grid-cols-3 gap-2">
              <input type="number" min="0" step="any" value={form.cgst_pct}
                onChange={(e) => setForm({ ...form, cgst_pct: e.target.value })}
                className={`${inputCls} font-mono`} placeholder="CGST %" data-testid="form-cgst" />
              <input type="number" min="0" step="any" value={form.sgst_pct}
                onChange={(e) => setForm({ ...form, sgst_pct: e.target.value })}
                className={`${inputCls} font-mono`} placeholder="SGST %" data-testid="form-sgst" />
              <input type="number" min="0" step="any" value={form.igst_pct}
                onChange={(e) => setForm({ ...form, igst_pct: e.target.value })}
                className={`${inputCls} font-mono`} placeholder="IGST %" data-testid="form-igst" />
            </div>
            <div className="text-[10px] text-slate-400 mt-1">Combined GST: <span className="font-mono">{fmt(effectiveGstPct)}%</span></div>
          </Field>

          {/* Invoice Value (auto) */}
          <Field label="Invoice Value (auto)">
            <div className={`${readonlyCls} font-semibold text-emerald-800`} data-testid="form-invoice-value">₹{fmt(invoiceValue)}</div>
          </Field>

          {/* Vehicle Number */}
          <Field label="Vehicle Number">
            <input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })}
              className={`${inputCls} font-mono`} placeholder="MH12-AB-1234" data-testid="form-vehicle" />
          </Field>

          {/* DN — Delivery Note (optional) */}
          <Field label={<>DN <span className="text-slate-400 font-normal">(optional)</span></>}>
            <input value={form.dn} onChange={(e) => setForm({ ...form, dn: e.target.value })}
              className={`${inputCls} font-mono`} placeholder="DN-001" data-testid="form-dn" />
          </Field>

          {/* HSN Code */}
          <Field label="HSN Code">
            <input value={form.hsn_code} onChange={(e) => setForm({ ...form, hsn_code: e.target.value })}
              className={`${inputCls} font-mono`} placeholder="73181500" data-testid="form-hsn" />
          </Field>

          {/* Transport */}
          <Field label="Transport">
            <input value={form.transport_name} onChange={(e) => setForm({ ...form, transport_name: e.target.value })}
              className={inputCls} placeholder="VRL Logistics" data-testid="form-transport" />
          </Field>

          {/* Bill-To searchable dropdown (customer's addresses) */}
          <Field label="Bill To">
            {(() => {
              const cm = findCustomer(customers, form.customer);
              const addrs = Array.isArray(cm?.addresses) && cm.addresses.length
                ? cm.addresses
                : (cm?.address ? [{ label: "Primary", address: cm.address, place_of_supply: cm.place_of_supply || "" }] : []);
              if (!addrs.length) {
                return (
                  <>
                    <input value={form.bill_to_address}
                      onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })}
                      className={inputCls} placeholder="Enter bill-to address" data-testid="form-bill-to" />
                    {form.customer && (
                      <div className="text-[10px] text-amber-700 mt-1">
                        {cm
                          ? <>No saved addresses for <b>{cm.name}</b> — add them in Customer Master to pick from a dropdown.</>
                          : <>No Customer Master entry matched <b>{form.customer}</b> — typing freely. Add this customer in Customer Master to enable address dropdowns.</>}
                      </div>
                    )}
                  </>
                );
              }
              return (
                <SearchSelect
                  value={form.bill_to_address}
                  displayValue={form.bill_to_address ? `${addrs.find(a => a.address === form.bill_to_address)?.label || ""}${addrs.find(a => a.address === form.bill_to_address)?.label ? " — " : ""}${form.bill_to_address.slice(0,60)}${form.bill_to_address.length > 60 ? "…" : ""}` : ""}
                  onSelect={(v, opt) => setForm({ ...form, bill_to_address: v, bill_to_place_of_supply: opt?.meta?.place_of_supply || form.bill_to_place_of_supply })}
                  placeholder="Search bill-to address…"
                  testid="form-bill-to"
                  options={addrs.map((a) => ({
                    value: a.address,
                    label: <span>{a.label ? <b>{a.label}</b> : null}{a.label ? " — " : ""}{a.address}</span>,
                    search: `${a.label || ""} ${a.address || ""} ${a.place_of_supply || ""}`,
                    meta: a,
                  }))}
                />
              );
            })()}
          </Field>

          {/* Ship-To searchable dropdown */}
          <Field label="Ship To">
            {(() => {
              const cm = findCustomer(customers, form.customer);
              const addrs = Array.isArray(cm?.addresses) && cm.addresses.length
                ? cm.addresses
                : (cm?.address ? [{ label: "Primary", address: cm.address, place_of_supply: cm.place_of_supply || "" }] : []);
              if (!addrs.length) {
                return (
                  <input value={form.ship_to_address}
                    onChange={(e) => setForm({ ...form, ship_to_address: e.target.value })}
                    className={inputCls} placeholder="Enter ship-to address" data-testid="form-ship-to" />
                );
              }
              return (
                <SearchSelect
                  value={form.ship_to_address}
                  displayValue={form.ship_to_address ? `${addrs.find(a => a.address === form.ship_to_address)?.label || ""}${addrs.find(a => a.address === form.ship_to_address)?.label ? " — " : ""}${form.ship_to_address.slice(0,60)}${form.ship_to_address.length > 60 ? "…" : ""}` : ""}
                  onSelect={(v, opt) => setForm({ ...form, ship_to_address: v, ship_to_place_of_supply: opt?.meta?.place_of_supply || form.ship_to_place_of_supply })}
                  placeholder="Search ship-to address…"
                  testid="form-ship-to"
                  options={addrs.map((a) => ({
                    value: a.address,
                    label: <span>{a.label ? <b>{a.label}</b> : null}{a.label ? " — " : ""}{a.address}</span>,
                    search: `${a.label || ""} ${a.address || ""} ${a.place_of_supply || ""}`,
                    meta: a,
                  }))}
                />
              );
            })()}
          </Field>

          {/* Remark — spans full width */}
          <Field label="Remark" className="md:col-span-2 xl:col-span-3">
            <textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })}
              rows={2}
              className={`${inputCls} min-h-[44px]`}
              placeholder="Any note to print on the invoice (e.g., Material packed in 5 boxes…)"
              data-testid="form-remark" />
          </Field>

        </div>

        {/* ===== Additional Items — fully unlocked, pick any part from any PO ===== */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40">
          {(() => {
            // Count of distinct parts already registered on the primary PO →
            // helps the user understand why the row "Part" dropdown may be
            // empty (no extra parts on this PO in Customer PO Register).
            const samePOParts = form.customer && form.po_no
              ? pos.filter((p) => sameCust(p.customer, form.customer) && p.po_no === form.po_no)
              : [];
            const otherParts = samePOParts.filter((p) => p.part_no !== form.part_no).length;
            return (
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Additional Items <span className="text-slate-400 font-normal">({extraItems.length})</span></div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Bill multiple parts on the same invoice. PO No defaults to the primary PO — you can switch it per row.
                    {form.po_no ? (
                      <span className={`ml-2 ${otherParts === 0 ? "text-amber-600" : "text-emerald-700"}`}>
                        · {samePOParts.length} part{samePOParts.length === 1 ? "" : "s"} registered on PO {form.po_no}
                        {otherParts === 0 && samePOParts.length > 0
                          ? " (only the primary — add more parts in Customer PO Register, or switch PO No on the row)"
                          : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={addExtraItem}
                  data-testid="add-extra-item-btn"
                  className="text-xs font-semibold text-[#1D2951] border border-[#1D2951] hover:bg-[#1D2951] hover:text-white px-3 py-1.5 rounded-md inline-flex items-center gap-1.5">
                  <span className="text-base leading-none">+</span> Add Item
                </button>
              </div>
            );
          })()}

          {extraItems.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic py-2">
              Click "Add Item" to bill another part. PO No defaults to the primary PO — you can switch it per row.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-slate-200" data-testid="extra-items-table">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="px-2 py-1.5 text-left font-semibold">PO No</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Part</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Balance</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Disp Qty</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Rate</th>
                    <th className="px-2 py-1.5 text-left font-semibold">HSN</th>
                    <th className="px-2 py-1.5 text-right font-semibold">CGST %</th>
                    <th className="px-2 py-1.5 text-right font-semibold">SGST %</th>
                    <th className="px-2 py-1.5 text-right font-semibold">IGST %</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Line Value</th>
                    <th className="px-2 py-1.5 text-center font-semibold w-12">&nbsp;</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {extraItems.map((row, i) => {
                    const lineValue = Number(row.dispatch_qty || 0) * Number(row.part_rate || 0);
                    const rowPo = row.po_no || "";
                    // Filter for the Part dropdown: same customer + same row PO,
                    // exclude only the rows already picked on a *different* extra row.
                    // We intentionally DO NOT exclude the primary part — the user
                    // can legitimately re-bill it on a new line. We also DO NOT
                    // filter on `balance_qty > 0` here so partially-dispatched lines
                    // still show up (locks elsewhere will still validate on save).
                    const optsForRow = pos.filter(
                      (p) =>
                        sameCust(p.customer, form.customer) &&
                        p.po_no === rowPo &&
                        (!extraItems.some((r, j) => j !== i && r.sales_po_id === p.id))
                    );
                    return (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 min-w-[140px]">
                          <SearchSelect
                            sm mono
                            value={rowPo}
                            onSelect={(v) => updateExtra(i, { po_no: v })}
                            placeholder="PO No…"
                            testid={`extra-item-pono-${i}`}
                            options={[
                              ...customerPoOpts.map((pn) => ({
                                value: pn,
                                label: <span className="font-mono">{pn}{pn === form.po_no ? <span className="text-[10px] text-slate-500"> (primary)</span> : null}</span>,
                                search: pn,
                              })),
                              ...(rowPo && !customerPoOpts.includes(rowPo)
                                ? [{ value: rowPo, label: <span className="font-mono">{rowPo}</span>, search: rowPo }]
                                : []),
                            ]}
                          />
                        </td>
                        <td className="px-2 py-1.5 min-w-[200px]">
                          <SearchSelect
                            sm
                            value={row.sales_po_id}
                            displayValue={row.sales_po_id ? `${row.part_no}${row.part_name ? " — " + row.part_name : ""}` : ""}
                            onSelect={(v) => updateExtra(i, { sales_po_id: v })}
                            placeholder="Search part…"
                            testid={`extra-item-part-${i}`}
                            options={[
                              ...optsForRow.map((p) => ({
                                value: p.id,
                                label: <span><span className="font-mono">{p.part_no}</span> — {p.part_name || "Item"} <span className="text-[10px] text-slate-400">· Bal {p.balance_qty}</span></span>,
                                search: `${p.part_no} ${p.part_name || ""}`,
                              })),
                              ...(row.sales_po_id && !optsForRow.some((p) => p.id === row.sales_po_id)
                                ? [{ value: row.sales_po_id, label: <span><span className="font-mono">{row.part_no}</span> — {row.part_name || "Item"}</span>, search: `${row.part_no} ${row.part_name || ""}` }]
                                : []),
                            ]}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-500">{fmt(row.balance_qty)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" step="any" value={row.dispatch_qty}
                            onChange={(e) => updateExtra(i, { dispatch_qty: e.target.value })}
                            data-testid={`extra-item-qty-${i}`}
                            className="w-24 text-right bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" step="any" value={row.part_rate}
                            onChange={(e) => updateExtra(i, { part_rate: e.target.value })}
                            data-testid={`extra-item-rate-${i}`}
                            className="w-24 text-right bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5">
                          <input value={row.hsn_code}
                            onChange={(e) => updateExtra(i, { hsn_code: e.target.value })}
                            data-testid={`extra-item-hsn-${i}`}
                            className="w-24 bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" step="any" value={row.cgst_pct ?? ""}
                            onChange={(e) => updateExtra(i, { cgst_pct: e.target.value })}
                            data-testid={`extra-item-cgst-${i}`}
                            placeholder="—"
                            className="w-14 text-right bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" step="any" value={row.sgst_pct ?? ""}
                            onChange={(e) => updateExtra(i, { sgst_pct: e.target.value })}
                            data-testid={`extra-item-sgst-${i}`}
                            placeholder="—"
                            className="w-14 text-right bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <input type="number" min="0" step="any" value={row.igst_pct ?? ""}
                            onChange={(e) => updateExtra(i, { igst_pct: e.target.value })}
                            data-testid={`extra-item-igst-${i}`}
                            placeholder="—"
                            className="w-14 text-right bg-white border border-slate-300 rounded px-1.5 py-1 text-xs font-mono" />
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-900">{fmt(lineValue)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <button type="button" onClick={() => removeExtra(i)}
                            data-testid={`extra-item-remove-${i}`}
                            className="text-rose-600 hover:text-rose-700"
                            title="Remove item">
                            <X size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end bg-slate-50">
          <button onClick={onSave} disabled={saving} data-testid="save-and-generate-invoice-btn"
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2 rounded-md inline-flex items-center gap-2">
            <Truck size={14} /> {saving ? "Generating…" : "Save & Generate Invoice"}
          </button>
        </div>
      </div>

      {/* ===== Sales Log ===== */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-semibold text-slate-900">Sales Log <span className="text-slate-400 font-normal">({filteredInvoices.length})</span></div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedIds.size > 0 && (
              <div
                className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 rounded-md px-3 py-1.5"
                data-testid="sales-log-bulk-bar"
              >
                <span className="text-xs font-semibold text-emerald-800">
                  {selectedIds.size} selected
                </span>
                <button
                  onClick={() => { setBulkCompanyValue(""); setBulkCompanyOpen(true); }}
                  data-testid="sales-log-bulk-set-company"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1 rounded-md inline-flex items-center gap-1"
                >
                  Set Company
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="sales-log-bulk-clear"
                  className="text-xs text-slate-600 hover:text-slate-900"
                  title="Clear selection"
                >Clear</button>
              </div>
            )}
            <div className="relative w-full max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={logSearch} onChange={(e) => setLogSearch(e.target.value)}
                placeholder="Search invoice / PO / customer / part…"
                className={`${inputCls} pl-9`} data-testid="log-search" />
            </div>
            <TableExportButton
              testIdPrefix="sales-log-export"
              filename="sales_log"
              rows={filteredInvoices}
              dateField="invoice_date"
              columns={[
                { key: "invoice_no", label: "Invoice No" },
                { key: "invoice_date", label: "Date" },
                { key: "company_name", label: "Company" },
                { key: "customer", label: "Customer" },
                { key: "part_no", label: "Part No" },
                { key: "part_name", label: "Part Name" },
                { key: "po_no", label: "PO No" },
                { key: "dispatch_qty", label: "Dispatch Qty" },
                { key: "part_rate", label: "Part Rate" },
                { key: "dispatch_value", label: "Dispatch Value" },
                { key: "gst_amount", label: "GST Amount" },
                { key: "invoice_value", label: "Invoice Value" },
                { key: "vehicle_no", label: "Vehicle No" },
                { key: "hsn_code", label: "HSN Code" },
              ]}
            />
          </div>
        </div>
        <div className="overflow-x-auto rounded-md border border-slate-200" data-testid="sales-log-table">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-[#1D2951] text-white">
                <LogTh align="center">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    data-testid="sales-log-select-all"
                    title="Select all visible invoices"
                    className="cursor-pointer"
                  />
                </LogTh>
                <LogTh>Invoice No</LogTh>
                <LogTh>Date</LogTh>
                <LogTh>Company</LogTh>
                <LogTh>Customer</LogTh>
                <LogTh>Part No</LogTh>
                <LogTh>Part Name</LogTh>
                <LogTh>PO No</LogTh>
                <LogTh align="right">Order Qty</LogTh>
                <LogTh align="right">Part Rate</LogTh>
                <LogTh align="right">Disp Qty</LogTh>
                <LogTh align="right">Disp Value</LogTh>
                <LogTh align="right">GST</LogTh>
                <LogTh align="right">Invoice Value</LogTh>
                <LogTh align="right">Total Invoice Value</LogTh>
                <LogTh>Vehicle No</LogTh>
                <LogTh>HSN Code</LogTh>
                <LogTh align="center">Actions</LogTh>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 && (
                <tr><td colSpan={18} className="text-center text-slate-400 py-8 bg-white">No invoices yet — fill the form above and click Save &amp; Generate Invoice</td></tr>
              )}
              {filteredInvoices.flatMap((inv, idx) => {
                const parent = pos.find((p) => p.id === inv.sales_po_id);
                // Each invoice may carry an `items` array (multi-item). When present we
                // explode it into one row per line item — all rows share the invoice number,
                // date, customer, vehicle, HSN etc. The Actions cell + Grand Invoice Value
                // are rendered only on the first row of each group so the print/edit/delete
                // buttons aren't duplicated and the total is unambiguous.
                const rawItems = Array.isArray(inv.items) && inv.items.length > 0 ? inv.items : null;
                const lines = rawItems || [{
                  part_no: inv.part_no, part_name: inv.part_name,
                  dispatch_qty: inv.dispatch_qty, part_rate: inv.part_rate,
                  dispatch_value: inv.dispatch_value, gst_pct: inv.gst_pct, gst_amount: inv.gst_amount,
                  line_total: inv.invoice_value, sales_po_id: inv.sales_po_id, po_no: inv.po_no,
                }];
                const stripeCls = idx % 2 === 0 ? "bg-[#FFFDF0]" : "bg-white";
                const isSelected = selectedIds.has(inv.id);
                return lines.map((li, li_idx) => {
                  const isFirst = li_idx === 0;
                  const lineParent = pos.find((p) => p.id === li.sales_po_id) || parent;
                  return (
                    <tr key={`${inv.id}-${li_idx}`}
                        className={`${stripeCls} ${!isFirst ? "border-t-0" : "border-t-2 border-slate-200"} ${isSelected ? "bg-emerald-50/80" : ""}`}
                        data-testid={isFirst ? `invoice-row-${inv.invoice_no}` : `invoice-row-${inv.invoice_no}-line-${li_idx}`}>
                      <Td align="center">
                        {isFirst ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelected(inv.id)}
                            data-testid={`sales-log-select-${inv.invoice_no}`}
                            className="cursor-pointer"
                          />
                        ) : null}
                      </Td>
                      <Td className="font-mono font-semibold text-[#1D2951]">
                        {isFirst ? inv.invoice_no : (
                          <span className="text-slate-400 text-[10px] italic">↳ {inv.invoice_no}</span>
                        )}
                      </Td>
                      <Td>{isFirst ? inv.invoice_date : ""}</Td>
                      <Td className="text-[11px]">
                        {isFirst ? (
                          inv.company_name
                            ? <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-1.5 py-0.5 font-medium">{inv.company_name}</span>
                            : <span className="text-slate-300 italic">Untagged</span>
                        ) : ""}
                      </Td>
                      <Td className="font-semibold">{isFirst ? inv.customer : ""}</Td>
                      <Td className="font-mono">{li.part_no || "—"}</Td>
                      <Td>{li.part_name || lineParent?.part_name || "—"}</Td>
                      <Td className="font-mono">{li.po_no || (isFirst ? inv.po_no : "")}</Td>
                      <Td align="right" className="font-mono">{lineParent ? Number(lineParent.order_qty || 0) : "—"}</Td>
                      <Td align="right" className="font-mono">{fmt(li.part_rate)}</Td>
                      <Td align="right" className="font-mono text-emerald-700 font-semibold">{Number(li.dispatch_qty || 0)}</Td>
                      <Td align="right" className="font-mono">{fmt(li.dispatch_value)}</Td>
                      <Td align="right" className="font-mono">{fmt(li.gst_amount)} <span className="text-slate-400 text-[10px]">({li.gst_pct}%)</span></Td>
                      <Td align="right" className="font-mono font-semibold text-[#7C3AED]">
                        {isFirst ? (
                          rawItems
                            ? <span title="Invoice total (all line items + GST)">{fmt(inv.invoice_value)} <span className="text-slate-400 text-[10px]">total</span></span>
                            : fmt(inv.invoice_value)
                        ) : (
                          <span className="text-slate-400">{fmt(li.line_total)}</span>
                        )}
                      </Td>
                      <Td align="right" className="font-mono font-bold text-emerald-700"
                          title="Sum of every line item on this invoice (incl. GST). Same value shown on every line of a multi-item invoice.">
                        {fmt(inv.invoice_value)}
                      </Td>
                      <Td className="font-mono">{isFirst ? (inv.vehicle_no || "—") : ""}</Td>
                      <Td className="font-mono">{li.hsn_code || (isFirst ? inv.hsn_code : "—")}</Td>
                      <Td align="center">
                        {isFirst ? (
                          <div className="inline-flex gap-1">
                            <button onClick={() => navigate(`/sales/invoice/print/${inv.id}`)}
                              title="Print invoice / e-way bill"
                              data-testid={`print-invoice-${inv.invoice_no}`}
                              className="p-1.5 text-[#1D2951] hover:bg-slate-100 rounded">
                              <Printer size={14} />
                            </button>
                            <button onClick={() => openEdit(inv)} title="Edit invoice (password required)"
                              data-testid={`edit-invoice-${inv.invoice_no}`}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded">
                              <Pencil size={14} />
                            </button>
                            {isAdmin && (
                              <button onClick={() => onDeleteInvoice(inv)} title="Delete invoice"
                                className="p-1.5 text-rose-600 hover:bg-rose-50 rounded">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ) : null}
                      </Td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== Bulk Set Company modal ===== */}
      {bulkCompanyOpen && (
        <Modal title={`Set Company for ${selectedIds.size} invoice${selectedIds.size === 1 ? "" : "s"}`} small onClose={() => setBulkCompanyOpen(false)}>
          <div className="text-xs text-slate-500 mb-2">
            The tagged company drives which sub-company these invoices count under on MR & Accounts Dashboard.
          </div>
          <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Company</label>
          <select
            value={bulkCompanyValue}
            onChange={(e) => setBulkCompanyValue(e.target.value)}
            data-testid="sales-log-bulk-company-select"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
          >
            <option value="">— Clear tag (leave unassigned) —</option>
            {companyOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {companyOptions.length === 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
              No companies defined in Company Master. Add at least one to tag invoices.
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => setBulkCompanyOpen(false)}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-md"
              data-testid="sales-log-bulk-cancel"
            >Cancel</button>
            <button
              onClick={applyBulkCompany}
              disabled={bulkSaving}
              data-testid="sales-log-bulk-confirm"
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md"
            >{bulkSaving ? "Saving…" : (bulkCompanyValue ? "Apply Tag" : "Clear Tag")}</button>
          </div>
        </Modal>
      )}

      {/* ===== Print prompt — Invoice OR E-Way Bill ===== */}
      {printPrompt && (
        <Modal title="Invoice Generated" small onClose={() => setPrintPrompt(null)}>
          <div className="text-sm text-slate-700">
            Invoice <span className="font-mono font-semibold">{printPrompt.invoice_no}</span> has been added to the Sales Log.
          </div>
          <div className="text-xs text-slate-500 mt-1">Choose what you'd like to print (both render as A4):</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            <button onClick={() => { const id = printPrompt.id; setPrintPrompt(null); navigate(`/sales/invoice/print/${id}?print=invoice`); }}
              data-testid="print-prompt-invoice"
              className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center justify-center gap-2">
              <FileText size={14} /> Print Invoice (A4)
            </button>
            <button onClick={() => { const id = printPrompt.id; setPrintPrompt(null); navigate(`/sales/invoice/print/${id}?print=eway`); }}
              data-testid="print-prompt-eway"
              className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center justify-center gap-2">
              <FileSpreadsheet size={14} /> Print E-Way Bill (A4)
            </button>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setPrintPrompt(null)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md" data-testid="print-prompt-skip">Not now</button>
          </div>
        </Modal>
      )}

      {/* ===== Edit Invoice modal (password-gated, full form) ===== */}
      {editInv && (() => {
        // Live preview of the totals — sum across every line item
        const _lines = editInv.items || [];
        const _hasPerHead = (editInv.cgst_pct !== "" || editInv.sgst_pct !== "" || editInv.igst_pct !== "");
        const _invHeadPct = _hasPerHead
          ? Number(editInv.cgst_pct || 0) + Number(editInv.sgst_pct || 0) + Number(editInv.igst_pct || 0)
          : null;
        let _dv = 0, _ga = 0;
        for (const li of _lines) {
          const q = Number(li.dispatch_qty || 0);
          const r = Number(li.part_rate || 0);
          const dv = +(q * r).toFixed(2);
          const gp = Number(li.gst_pct || 0);
          _dv += dv;
          _ga += +(dv * gp / 100).toFixed(2);
        }
        _dv = +_dv.toFixed(2);
        _ga = +_ga.toFixed(2);
        const _iv = +(_dv + _ga).toFixed(2);
        const _gp = _invHeadPct != null ? _invHeadPct : (_lines[0]?.gst_pct || 0);
        const updateLine = (idx, patch) => {
          setEditInv((prev) => ({
            ...prev,
            items: (prev.items || []).map((li, i) => i === idx ? { ...li, ...patch } : li),
          }));
        };
        return (
          <Modal title={`Edit Invoice — ${editInv.invoice_no}`} onClose={() => { setEditInv(null); setEditPwd(""); }} large>
            {/* Header row — Invoice No + Date */}
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 text-xs text-amber-900">
              All fields below are editable. Saving requires the <b>Invoice-Edit password (INV@2004)</b>.
              Changing the invoice number is allowed but must remain unique. Parent PO linkage stays intact —
              if you mistyped the wrong PO during dispatch, delete this invoice and recreate it instead.
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Invoice No *">
                <input value={editInv.invoice_no}
                  onChange={(e) => setEditInv({ ...editInv, invoice_no: e.target.value })}
                  className={inputCls + " font-mono font-semibold text-[#1D2951]"} data-testid="edit-inv-no" />
              </Field>
              <Field label="Invoice Date *">
                <input type="date" value={editInv.invoice_date}
                  onChange={(e) => setEditInv({ ...editInv, invoice_date: e.target.value })}
                  className={inputCls + " font-mono"} data-testid="edit-inv-date" />
              </Field>
              <Field label="Customer">
                <input value={editInv.customer}
                  onChange={(e) => setEditInv({ ...editInv, customer: e.target.value })}
                  className={inputCls} data-testid="edit-inv-customer" />
              </Field>
            </div>

            {/* ===== Multi-item line table — every part on this invoice is editable ===== */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Line Items <span className="text-slate-400 font-normal normal-case">({_lines.length})</span>
                </div>
              </div>
              <div className="overflow-x-auto border border-slate-200 rounded-md">
                <table className="w-full text-xs border-collapse" data-testid="edit-inv-items-table">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-600 uppercase tracking-wider">
                      <th className="px-2 py-1.5 text-left w-8">#</th>
                      <th className="px-2 py-1.5 text-left">Part No</th>
                      <th className="px-2 py-1.5 text-left">Part Name</th>
                      <th className="px-2 py-1.5 text-left">HSN</th>
                      <th className="px-2 py-1.5 text-left">PO No</th>
                      <th className="px-2 py-1.5 text-right">Qty *</th>
                      <th className="px-2 py-1.5 text-right">Rate ₹</th>
                      <th className="px-2 py-1.5 text-right">GST %</th>
                      <th className="px-2 py-1.5 text-right">Line Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {_lines.map((li, idx) => {
                      const q = Number(li.dispatch_qty || 0);
                      const r = Number(li.part_rate || 0);
                      const dv = +(q * r).toFixed(2);
                      const gp = Number(li.gst_pct || 0);
                      const ga = +(dv * gp / 100).toFixed(2);
                      const lt = +(dv + ga).toFixed(2);
                      return (
                        <tr key={idx} className="border-t border-slate-100" data-testid={`edit-inv-line-${idx}`}>
                          <td className="px-2 py-1 font-mono font-semibold text-slate-500">{idx + 1}</td>
                          <td className="px-2 py-1">
                            <input value={li.part_no || ""}
                              onChange={(e) => updateLine(idx, { part_no: e.target.value })}
                              className="w-full border border-slate-300 rounded px-1.5 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-part-no`} />
                          </td>
                          <td className="px-2 py-1">
                            <input value={li.part_name || ""}
                              onChange={(e) => updateLine(idx, { part_name: e.target.value })}
                              className="w-full border border-slate-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-part-name`} />
                          </td>
                          <td className="px-2 py-1">
                            <input value={li.hsn_code || ""}
                              onChange={(e) => updateLine(idx, { hsn_code: e.target.value })}
                              className="w-24 border border-slate-300 rounded px-1.5 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-hsn`} />
                          </td>
                          <td className="px-2 py-1">
                            <input value={li.po_no || ""}
                              onChange={(e) => updateLine(idx, { po_no: e.target.value })}
                              className="w-28 border border-slate-300 rounded px-1.5 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-po`} />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <input type="number" min="0" step="any" value={li.dispatch_qty}
                              onChange={(e) => updateLine(idx, { dispatch_qty: e.target.value })}
                              className="w-20 border border-slate-300 rounded px-1.5 py-1 text-xs font-mono text-right focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-qty`} />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <input type="number" min="0" step="any" value={li.part_rate}
                              onChange={(e) => updateLine(idx, { part_rate: e.target.value })}
                              className="w-24 border border-slate-300 rounded px-1.5 py-1 text-xs font-mono text-right focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-rate`} />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <input type="number" min="0" step="any" value={li.gst_pct}
                              onChange={(e) => updateLine(idx, { gst_pct: e.target.value })}
                              className="w-16 border border-slate-300 rounded px-1.5 py-1 text-xs font-mono text-right focus:outline-none focus:ring-1 focus:ring-[#1D2951]/40"
                              data-testid={`edit-inv-line-${idx}-gst`} />
                          </td>
                          <td className="px-2 py-1 text-right font-mono font-semibold text-emerald-700 whitespace-nowrap">
                            ₹{fmt(lt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr className="border-t-2 border-slate-200">
                      <td colSpan={5} className="px-2 py-1.5 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">Totals</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500">—</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">₹{fmt(_dv)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">₹{fmt(_ga)}</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700">₹{fmt(_iv)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Tip: changing any quantity / rate / GST recomputes the totals immediately. Each line&apos;s parent PO balance is re-validated on Save.
              </div>
            </div>

            {/* GST (invoice-level split — intra vs inter state) */}
            <div className="mt-3">
              <Field label={<>Per-head GST split <span className="text-slate-400 font-normal">(optional — applies to intra/inter state. Combined: {fmt(_gp)}%)</span></>}>
                <div className="grid grid-cols-3 gap-2">
                  <input type="number" min="0" step="any" value={editInv.cgst_pct}
                    onChange={(e) => setEditInv({ ...editInv, cgst_pct: e.target.value })}
                    className={inputCls + " font-mono"} placeholder="CGST %" data-testid="edit-inv-cgst" />
                  <input type="number" min="0" step="any" value={editInv.sgst_pct}
                    onChange={(e) => setEditInv({ ...editInv, sgst_pct: e.target.value })}
                    className={inputCls + " font-mono"} placeholder="SGST %" data-testid="edit-inv-sgst" />
                  <input type="number" min="0" step="any" value={editInv.igst_pct}
                    onChange={(e) => setEditInv({ ...editInv, igst_pct: e.target.value })}
                    className={inputCls + " font-mono"} placeholder="IGST %" data-testid="edit-inv-igst" />
                </div>
              </Field>
            </div>

            {/* Transport & Logistics */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="Vehicle No">
                <input value={editInv.vehicle_no}
                  onChange={(e) => setEditInv({ ...editInv, vehicle_no: e.target.value })}
                  className={inputCls + " font-mono"} placeholder="MH12-AB-1234" data-testid="edit-inv-vehicle" />
              </Field>
              <Field label="Transport">
                <input value={editInv.transport_name}
                  onChange={(e) => setEditInv({ ...editInv, transport_name: e.target.value })}
                  className={inputCls} placeholder="VRL Logistics" data-testid="edit-inv-transport" />
              </Field>
              <Field label="DN (Delivery Note)">
                <input value={editInv.dn}
                  onChange={(e) => setEditInv({ ...editInv, dn: e.target.value })}
                  className={inputCls + " font-mono"} placeholder="DN-001" data-testid="edit-inv-dn" />
              </Field>
            </div>

            {/* Addresses */}
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Bill To Address">
                <textarea rows={2} value={editInv.bill_to_address}
                  onChange={(e) => setEditInv({ ...editInv, bill_to_address: e.target.value })}
                  className={inputCls} data-testid="edit-inv-bill-to" />
              </Field>
              <Field label="Bill To — Place of Supply">
                <input value={editInv.bill_to_place_of_supply}
                  onChange={(e) => setEditInv({ ...editInv, bill_to_place_of_supply: e.target.value })}
                  className={inputCls} data-testid="edit-inv-bill-pos" />
              </Field>
              <Field label="Ship To Address">
                <textarea rows={2} value={editInv.ship_to_address}
                  onChange={(e) => setEditInv({ ...editInv, ship_to_address: e.target.value })}
                  className={inputCls} data-testid="edit-inv-ship-to" />
              </Field>
              <Field label="Ship To — Place of Supply">
                <input value={editInv.ship_to_place_of_supply}
                  onChange={(e) => setEditInv({ ...editInv, ship_to_place_of_supply: e.target.value })}
                  className={inputCls} data-testid="edit-inv-ship-pos" />
              </Field>
            </div>

            {/* Remark */}
            <div className="mt-3">
              <Field label="Remark">
                <textarea rows={2} value={editInv.remark}
                  onChange={(e) => setEditInv({ ...editInv, remark: e.target.value })}
                  className={inputCls} placeholder="Any note to print on the invoice" data-testid="edit-inv-remark" />
              </Field>
            </div>

            {/* Password */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <Field label="Invoice-Edit Password *">
                <div className="relative max-w-sm">
                  <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="password" value={editPwd}
                    onChange={(e) => setEditPwd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); }}
                    className={`${inputCls} pl-9`} placeholder="INV@2004" data-testid="edit-inv-password" />
                </div>
              </Field>
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setEditInv(null); setEditPwd(""); }} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
              <button onClick={saveEdit} disabled={editSaving}
                data-testid="edit-inv-save"
                className="bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-1.5 rounded-md inline-flex items-center gap-1.5">
                <Check size={14} /> {editSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* ===== Seed counter modal ===== */}
      {seedOpen && (
        <Modal title="Initialise Invoice Counter" onClose={() => setSeedOpen(false)} small>
          <p className="text-xs text-slate-500 mb-3">
            Enter the <span className="font-semibold">last invoice number you have already issued</span>. The next invoice generated by CPMS will be <span className="font-mono font-semibold">INV-(N+1)/YY-YY+1</span> (Indian FY). Once any invoice exists this value can no longer be changed.
          </p>
          <input type="number" min="0" value={seedVal} onChange={(e) => setSeedVal(e.target.value)}
            className={inputCls} data-testid="seed-counter-input" autoFocus />
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setSeedOpen(false)} className="px-3 py-1.5 text-sm border border-slate-300 rounded-md">Cancel</button>
            <button onClick={onSeedCounter} data-testid="seed-counter-save"
              className="bg-[#1D2951] hover:bg-[#2B3A67] text-white text-sm font-semibold px-4 py-1.5 rounded-md">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ----- Helpers
const Lbl = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">{children}</div>
);
const Field = ({ label, children }) => (
  <div>
    <Lbl>{label}</Lbl>
    {children}
  </div>
);
const LogTh = ({ children, align = "left" }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-${align}`}>{children}</th>
);
const Td = ({ children, align = "left", className = "" }) => (
  <td className={`px-3 py-2.5 text-${align} ${className}`}>{children}</td>
);

/**
 * Self-contained searchable dropdown (combobox-style). Use anywhere a plain
 * <select> would be — but with a search input that filters the option list.
 *
 * Props:
 *   - value       : currently-selected option `value` (string)
 *   - displayValue: shown in the input when the dropdown is closed
 *   - onSelect    : (value, option) => void
 *   - options     : Array<{ value, label, search? }>  — `search` is what the
 *                   typed text is matched against; falls back to label / value.
 *   - placeholder, disabled, mono, testid, sm? (compact for table rows)
 */
function SearchSelect({ value, displayValue, onSelect, options, placeholder, disabled, mono, testid, sm }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      if (inputRef.current) setRect(inputRef.current.getBoundingClientRect());
    };
    const onDoc = (e) => {
      // Treat the portaled panel as "inside" too — clicks on its options must
      // not close the dropdown before the click handler can fire.
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    recompute();
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [open]);

  const norm = (s) => String(s || "").toLowerCase();
  const filtered = (options || []).filter((o) => {
    if (!q) return true;
    const hay = norm(o.search != null ? o.search : `${o.value} ${typeof o.label === "string" ? o.label : ""}`);
    return hay.includes(norm(q));
  });
  const shown = open ? q : (displayValue ?? value ?? "");
  const baseInput = sm
    ? `w-full bg-white border border-slate-300 rounded px-1.5 py-1 text-xs ${mono ? "font-mono" : ""}`
    : `w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white ${mono ? "font-mono" : ""}`;
  // Make room on right for chevron + clear
  const padCls = sm ? "pl-6 pr-10" : "pl-9 pr-12";

  const openPanel = () => {
    setQ("");
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div ref={wrapRef} className="relative">
      <Search size={sm ? 11 : 14} className={`absolute ${sm ? "left-1.5" : "left-3"} top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none`} />
      <input
        ref={inputRef}
        value={shown}
        onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true); }}
        onFocus={openPanel}
        onClick={openPanel}
        placeholder={placeholder}
        disabled={disabled}
        className={`${baseInput} ${padCls} ${disabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testid}
      />
      {/* Chevron — makes it visually obvious that it's a dropdown */}
      <ChevronDown
        size={sm ? 12 : 14}
        className={`absolute ${sm ? "right-1.5" : "right-3"} top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
      />
      {/* Clear (only when a value is set) — positioned just left of the chevron */}
      {value && !disabled && (
        <button type="button"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onSelect("", null); setQ(""); setOpen(false); }}
          className={`absolute ${sm ? "right-5" : "right-7"} top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-rose-600`}>
          <X size={sm ? 11 : 13} />
        </button>
      )}
      {/* Portal-rendered dropdown panel — escapes any overflow:hidden parent */}
      {open && rect && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: rect.bottom + 4, left: rect.left, width: rect.width, maxHeight: 240 }}
          className="z-[9999] bg-white border border-slate-200 rounded-md shadow-lg overflow-y-auto"
          data-testid={`${testid}-dropdown`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400 italic">No matches</div>
          ) : filtered.map((opt) => (
            <button key={opt.value || opt.label} type="button"
              onClick={() => { onSelect(opt.value, opt); setOpen(false); setQ(""); }}
              className={`w-full text-left ${sm ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm"} hover:bg-slate-50 flex items-center gap-2`}>
              <span className="flex-1">{opt.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function DropdownInput({ value, onChange, onFocus, onClear, placeholder, disabled, mono, testid, open, options }) {
  const inputCls = `w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white ${mono ? "font-mono" : ""} pl-9 pr-9`;
  return (
    <div className="relative">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => { e.target.select(); onFocus && onFocus(); }}
        onClick={(e) => { e.target.select(); }}
        placeholder={placeholder}
        disabled={disabled}
        className={`${inputCls} ${disabled ? "bg-slate-50 text-slate-400 cursor-not-allowed" : ""}`}
        data-testid={testid}
      />
      {value && !disabled && (
        <button onClick={onClear} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-rose-600">
          <X size={14} />
        </button>
      )}
      {open && options.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {options.map((opt) => (
            <button key={opt.key} onClick={opt.onClick}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
              <Check size={12} className="opacity-0 group-hover:opacity-100 text-emerald-600" />
              <span className="flex-1">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, small = false, large = false }) {
  const widthCls = large ? "max-w-5xl" : small ? "max-w-md" : "max-w-2xl";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-lg shadow-xl border border-slate-200 w-full ${widthCls} p-6 max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-slate-900">{title}</div>
          <button onClick={onClose} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
